import { ComponentRef, createNgModule, Injector, NgModuleRef, Type, ViewContainerRef } from '@angular/core'

class LazyLoadCancelledError extends Error {}

export interface LazyLoadComponentConfig {
  import: () => Promise<{ [key: string]: any }>
  component: string
  module?: string
  skeleton?: Type<unknown>
  waitForContentReady?: boolean
}

export const lazyLoadFeature = async <T>(
  container: ViewContainerRef,
  component: Type<T>,
  module: Type<any> | null,
  injector: Injector,
  loaderPromise?: Promise<void>,
  shouldCreate?: () => boolean
): Promise<ComponentRef<T>> => {
  const loaders = []
  const options = { injector }
  let ngModuleRef: NgModuleRef<any> | undefined

  if (loaderPromise) loaders.push(loaderPromise)

  if (module) {
    ngModuleRef = createNgModule(module, injector)
    const translationsLoader = (ngModuleRef.instance as any).translationsLoader
    Object.assign(options, { ngModuleRef, injector: ngModuleRef.injector })
    if (translationsLoader) loaders.push(translationsLoader.load())
  }

  try {
    await Promise.all(loaders)
  } catch (error) {
    ngModuleRef?.destroy()
    throw error
  }

  if (shouldCreate && !shouldCreate()) {
    ngModuleRef?.destroy()
    throw new LazyLoadCancelledError()
  }

  container.clear()

  let componentRef: ComponentRef<T>
  try {
    componentRef = container.createComponent(component, options)
  } catch (error) {
    ngModuleRef?.destroy()
    throw error
  }

  if (ngModuleRef) componentRef.onDestroy(() => ngModuleRef.destroy())

  return componentRef
}

export class LazyLoad {
  static component<T>(
    loader: (() => Promise<{ [key: string]: any }>) | LazyLoadComponentConfig,
    ref: ViewContainerRef,
    injector: Injector
  ): Promise<ComponentRef<T>>
  static component<T>(
    loader: (() => Promise<{ [key: string]: any }>) | LazyLoadComponentConfig,
    ref: ViewContainerRef,
    injector: Injector,
    shouldCreate: () => boolean
  ): Promise<ComponentRef<T> | undefined>
  static async component<T>(
    loader: (() => Promise<{ [key: string]: any }>) | LazyLoadComponentConfig,
    ref: ViewContainerRef,
    injector: Injector,
    shouldCreate?: () => boolean
  ): Promise<ComponentRef<T> | undefined> {
    const module = await (typeof loader === 'function' ? loader() : loader.import())
    if (shouldCreate && !shouldCreate()) return undefined

    const componentClass = typeof loader === 'function'
      ? Object.values(module).find(v => typeof v === 'function') as Type<T>
      : module[loader.component] as Type<T>
    const moduleClass = typeof loader === 'function' || !loader.module
      ? null
      : module[loader.module] as Type<any>

    if (!componentClass) throw new Error('Lazy loaded component was not found')
    if (typeof loader !== 'function' && loader.module && !moduleClass) {
      throw new Error('Lazy loaded module was not found')
    }

    let component: ComponentRef<T>
    try {
      component = await lazyLoadFeature<T>(ref, componentClass, moduleClass, injector, undefined, shouldCreate)
    } catch (error) {
      if (error instanceof LazyLoadCancelledError) return undefined
      throw error
    }
    component.changeDetectorRef.detectChanges()
    return component
  }
}
